/* eslint-disable space-before-function-paren */

import { useState } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import BackNavigation from 'src/components/common/backNavigation/BackNavigation';
import Button from 'src/components/button/Button';
import Typography from 'src/components/common/typography/Typography';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { useGoogleLogin, GoogleLogin } from '@react-oauth/google';
import { componentSizeVariantsEnum } from 'src/constants/common.constants';
import {
  EyeIcon,
  EyeOffIcon,
  MailIcon,
  PasswordIcon,
  PersonIcon
} from 'src/components/common/common.icons';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { signup, signIn } from 'src/store/auth/auth.slice';
import { useUrlParamValue } from 'src/hooks';

import './signup.scss';
import signupValidationSchema from './signup.validationSchema';

import { APIResponse } from 'src/services/base.api.service';

import { TSignInResponse } from 'src/services/apiEndpoint.types.ts';
import GoogleLogo from 'src/assets/images/AuthLogo/g-logo.png';

// const navigate = useNavigate();

export type TSignupFormValues = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const SignUp = () => {
  const { loading, name, email } = useAppSelector((store) => store.auth);
  const navigate = useNavigate();

  /*
   ** Prefill the form with existing redux data
   */
  const initialValues: TSignupFormValues = {
    name: name || '',
    email: email || '',
    password: '',
    confirmPassword: ''
  };

    // Hooks
  const { paramValue } = useUrlParamValue();
  console.log("paramValue", paramValue);

  const dispatch = useAppDispatch();

  const prevRoute = paramValue({ paramName: 'prev' });
  const vc = paramValue({ paramName: 'partnerInvestor' });
  console.log("investorId", vc);

  const handleGoogleSubmit = async ({ email, password, googleToken }) => {
    // login api call
    dispatch(
      signIn({
        email,
        password,
        googleToken
      })
    ).then((response: unknown) => {
      const loginResponse = response as unknown as APIResponse<TSignInResponse>;
      const status: number = loginResponse?.status;
      const isOnboardingComplete: boolean = loginResponse?.data?.data?.user?.onboardingComplete;
      const user = loginResponse?.data?.data?.user;
      const userType = loginResponse?.data?.data?.type;
      const isMentorOrInvestor =
        user?.role === 'Mentor' ||
        user?.role === 'Investor' ||
        user?.role === 'Mentor and Investor';

      if (status === 200 && userType === 'User') {
        if (isOnboardingComplete) {
          // navigate(prevRoute || '/dashboard');
          navigate('/startup-onboarding');
        } else if (isMentorOrInvestor) {
          navigate('/tell-me-more?userType=MENTOR_INVESTOR');
        } else {
          navigate('/onboarding-user-type');
        }
      }
    });
  };

  // OAuth 2.0 Authorization Code Flow for getting refresh tokens
  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      console.log("Auth code received:", codeResponse.code);

      // Send the auth code to your backend
      dispatch(
        signIn({
          email: "null@gmail.com",
          password: "null",
          googleToken: codeResponse.code,
          referrerInvestorId: vc || null
        })
      ).then((response: unknown) => {
        const loginResponse = response as unknown as APIResponse<TSignInResponse>;
        const status: number = loginResponse?.status;
        const isOnboardingComplete: boolean = loginResponse?.data?.data?.user?.onboardingComplete;
        const user = loginResponse?.data?.data?.user;
        const userType = loginResponse?.data?.data?.type;
        const isMentorOrInvestor =
          user?.role === 'Mentor' ||
          user?.role === 'Investor' ||
          user?.role === 'Mentor and Investor';

        if (status === 200 && userType === 'User') {
          if (isOnboardingComplete && user.isGoUser) {
            navigate(prevRoute || '/score');
          } else if (isOnboardingComplete) {
            navigate(prevRoute || '/startup-onboarding');
          } else if (isMentorOrInvestor) {
            navigate('/tell-me-more?userType=MENTOR_INVESTOR');
          } else {
            navigate('/onboarding-user-type');
          }
        }
      });
    },
    onError: (error) => {
      console.log('Login Failed:', error);
    },
    scope: "email profile openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile"
  });

  const [maskPassword, setMaskPassword] = useState<{ password: boolean; confirmPassword: boolean }>(
    { password: true, confirmPassword: true }
  );

  const handleSubmit = async ({ name, email, password }: TSignupFormValues) => {
    // Signup api call
    dispatch(
      signup({
        name,
        email,
        password,
        referrerInvestorId: vc || null,
        navigateTo: '/startup-onboarding'
      })
    );
  };

  return (
    <div className="sign-up">
      {/* <BackNavigation text="Back" route={prevRoute || '/'} /> */}
      <Typography as="h3" variant="heading-3" fontWeight="semiBold" className="sign-up__heading">
        Create an Account!
      </Typography>
      <Typography as="p" variant="body-2" fontWeight="regular" className="sign-up__caption">
        Your journey starts here. Create your account today
      </Typography>

      {/* Form starts */}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={signupValidationSchema}
        enableReinitialize
      >
        {() => {
          return (
            <Form className="sign-up__form" noValidate>
              {/* NoValidate is used, because yup is used to validate the user input */}
              <InputField
                label="Enter Full Name"
                id="name"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Full Name"
                startIcon={<PersonIcon />}
                type="text"
              />
              <InputField
                label="Enter Email Address"
                id="email"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Email Address"
                startIcon={<MailIcon />}
                type="email"
              />
              <InputField
                label="Enter Password"
                id="password"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Password"
                startIcon={<PasswordIcon />}
                endIcon={maskPassword.password ? <EyeOffIcon /> : <EyeIcon />}
                maskField={maskPassword.password}
                endIconClickHandler={() =>
                  setMaskPassword((prev) => ({ ...prev, password: !prev.password }))
                }
              />
              <InputField
                label="Confirm password"
                id="confirmPassword"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Confirm password"
                startIcon={<PasswordIcon />}
                endIcon={maskPassword.confirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                maskField={maskPassword.confirmPassword}
                endIconClickHandler={() =>
                  setMaskPassword((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))
                }
              />
              <Button variant="primary" size="small" type="submit" loading={loading}>
                <Typography as="span" variant="body-2" fontWeight="semiBold">
                  Continue
                </Typography>
              </Button>
            </Form>
          );
        }}
      </Formik>
      {/* Form ends */}

      <div className="sign-up__footer">
        <Typography as="span" variant="body-2" fontWeight="medium">
          Already have an account?
        </Typography>
        {/* <Link to="/login?prev=/sign-up">Login</Link> */}
        <Link to={`/login?${vc ? `partnerInvestor=${vc}` : ''}`}>Login</Link>
      </div>
      {/* <GoogleLogin
        onSuccess={credentialResponse => {
          console.log(credentialResponse);
        }}
        onError={() => {
          console.log('Login Failed');
        }}
      /> */}
      {/* <GoogleLogin
        onSuccess={credentialResponse => {
          console.log(credentialResponse)
          handleGoogleSubmit(({
            email: "null@gmail.com",
            password: "null",
            googleToken: credentialResponse.credential
          }))
        }}
        onError={() => {
          console.log('Login Failed');
        }}
      /> */}
      {/* <div className="log-in__or-container">
        <div className="log-in__or-line"></div>
        <Typography as="span" variant="body-2" fontWeight="medium" className="log-in__or-text">
          OR
        </Typography>
        <div className="log-in__or-line"></div>
      </div> */}
      {/* <div className="google-login-button-container">
        <GoogleLogin
          text="continue_with"
          onSuccess={credentialResponse => {
            console.log(credentialResponse);
            handleGoogleSubmit({
              email: "null@gmail.com",
              password: "null",
              googleToken: credentialResponse.credential
            });
          }}
          onError={() => {
            console.log('Login Failed');
          }}
          containerProps={{ className: "google-login-button" }}
        />
      </div> */}
      <div className="google-login-button-container">
        <button
          onClick={() => login()}
          className="google-login-button custom-google-button"
        >
          <img
            src={GoogleLogo}
            alt="Google logo"
            className="google-icon"
          />
          <Typography as="span" variant="body-2" fontWeight="semiBold">
            Continue with Google
          </Typography>
        </button>
      </div>
    </div>
  );
};

export default SignUp;
